import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { sileo } from 'sileo';
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Download,
  Heart,
  Pencil,
  Instagram,
  Copy,
  Star,
  Trash2,
  Zap,
  Check,
  AlertCircle,
  Loader2,
  X,
  Menu,
  ArrowLeft,
  Search,
  Settings,
  User,
  Bell,
  Mail,
  MoreHorizontal,
  Plus,
  Filter,
  Calendar,
  FileText,
  Image,
  Share2,
  Flag,
  Eye,
  EyeOff,
  Archive,
  Link,
  Sparkles,
  Clock,
  Package,
  ShoppingBag,
} from 'lucide-react';

/* ── Catalog helper components ── */

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-6 mt-16 first:mt-0">
    <h2 className="text-[length:var(--text-page-title-mobile)] md:text-[length:var(--text-page-title-desktop)] font-semibold text-neutral-900 tracking-tight">{children}</h2>
    <div className="mt-2 h-px bg-neutral-200" />
  </div>
);

const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-8">
    <h3 className="text-[length:var(--text-caption)] font-medium text-neutral-400 uppercase tracking-wider mb-3">{title}</h3>
    {children}
  </div>
);

const CodeLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block text-[length:var(--text-caption)] font-mono bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-sm mb-1">
    {children}
  </span>
);

const BeforeAfter = ({ before, after, labelBefore, labelAfter }: { before: React.ReactNode; after: React.ReactNode; labelBefore?: string; labelAfter?: string }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="bg-white rounded-lg border border-neutral-200 p-5 relative">
      <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider text-error-text bg-error-soft px-2 py-0.5 rounded-sm">Antes</span>
      {labelBefore && <CodeLabel>{labelBefore}</CodeLabel>}
      <div className="mt-2">{before}</div>
    </div>
    <div className="bg-surface-card rounded-lg border border-neutral-200 p-5 relative">
      <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider text-success-text bg-success-soft px-2 py-0.5 rounded-sm">Después</span>
      {labelAfter && <CodeLabel>{labelAfter}</CodeLabel>}
      <div className="mt-2">{after}</div>
    </div>
  </div>
);

const NewCard = ({ children, label, shadow = false }: { children: React.ReactNode; label?: string; shadow?: boolean }) => (
  <div className={`bg-surface-card rounded-lg border border-neutral-200 p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] ${shadow ? 'shadow-sm' : ''}`}>
    {label && <CodeLabel>{label}</CodeLabel>}
    <div className="mt-2">{children}</div>
  </div>
);

/* ── Custom Select (no native dropdown) ── */
const CustomSelect = ({ options, placeholder, value, onChange }: { options: { value: string; label: string }[]; placeholder: string; value: string; onChange: (v: string) => void }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className={selected ? 'text-neutral-900' : 'text-neutral-400'}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[length:var(--text-body)] transition-colors ${
                value === opt.value ? 'bg-neutral-100 text-neutral-900 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Dropdown Menu ── */
const DropdownMenu = ({ trigger, children }: { trigger: React.ReactNode; children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        popupRef.current && !popupRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuW = 220;
      const menuH = 280;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pad = 12;

      const fitsRight = rect.left + menuW + pad < vw;
      const fitsBelow = rect.bottom + menuH + pad < vh;

      let left: number;
      if (fitsRight) {
        left = rect.left + window.scrollX;
      } else {
        left = Math.max(pad, rect.right + window.scrollX - menuW);
        if (left + menuW > vw - pad) left = vw - menuW - pad;
      }

      let top: number;
      let origin: string;
      if (fitsBelow) {
        top = rect.bottom + window.scrollY + 4;
        origin = fitsRight ? 'top left' : 'top right';
      } else {
        top = rect.top + window.scrollY - menuH - 4;
        if (top < pad + window.scrollY) top = pad + window.scrollY;
        origin = fitsRight ? 'bottom left' : 'bottom right';
      }

      setStyle({ top, left, zIndex: 9999, position: 'absolute', transformOrigin: origin });
    }
    setOpen(!open);
  }, [open]);

  return (
    <div ref={triggerRef} className="relative inline-block">
      <div onClick={handleToggle}>{trigger}</div>
      {open && ReactDOM.createPortal(
        <div
          ref={popupRef}
          className="min-w-[200px] bg-surface-card border border-neutral-200 rounded-md shadow-lg py-1 animate-fade-in"
          style={style}
        >
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            const el = child as React.ReactElement<{ onClick?: () => void }>;
            if ((el.type as any) === DropdownSeparator) return child;
            return React.cloneElement(el, {
              onClick: () => { el.props.onClick?.(); setOpen(false); },
            });
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const DropdownItem = ({ icon: Icon, label, desc, danger, onClick }: { icon?: React.ElementType; label: string; desc?: string; danger?: boolean; onClick?: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left flex items-start gap-3 px-3 ${desc ? 'py-2.5' : 'py-2'} transition-colors hover:bg-neutral-100 ${danger ? 'text-error' : 'text-neutral-900'}`}
  >
    {Icon && <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${danger ? 'text-error' : 'text-neutral-400'}`} />}
    <div className="flex-1 min-w-0">
      <p className={`text-[length:var(--text-body)] ${desc ? 'font-medium' : ''}`}>{label}</p>
      {desc && <p className="text-[length:var(--text-caption)] text-neutral-400 mt-0.5">{desc}</p>}
    </div>
  </button>
);

const DropdownSeparator = () => <div className="my-1 h-px bg-neutral-200" />;

/* ── Sheet / Sliding Panel ── */
const Sheet = ({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
  const [animated, setAnimated] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleClose = useCallback(() => {
    setAnimated(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    else setDragY(0);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] transition-all duration-300 ${animated ? 'bg-black/50 backdrop-blur-[2px]' : 'bg-transparent backdrop-blur-0'}`}
        onClick={handleClose}
      />
      {/* Desktop: slide from right */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] z-[61] flex-col bg-surface-card rounded-lg border border-neutral-200 shadow-lg overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">{title}</h3>
          <button onClick={handleClose} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        <div className="border-t border-neutral-200 px-6 py-3 flex justify-end gap-3">
          <button onClick={handleClose} className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Cancelar</button>
          <button onClick={handleClose} className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Aceptar</button>
        </div>
      </div>
      {/* Mobile: slide from bottom */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[61] bg-surface-card rounded-t-lg border-t border-neutral-200 shadow-lg flex flex-col"
        style={{
          height: 'calc(100dvh - 1rem)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          transform: dragY > 0
            ? `translateY(${dragY}px)`
            : animated ? 'translateY(0)' : 'translateY(100%)',
          transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
        }}
      >
        <div
          className="py-3 cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-10 h-1 bg-neutral-200 rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 border-b border-neutral-200">
          <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">{title}</h3>
          <button onClick={handleClose} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        <div className="border-t border-neutral-200 px-4 py-3 flex gap-3">
          <button onClick={handleClose} className="flex-1 h-[var(--btn-h)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] transition-colors">Cancelar</button>
          <button onClick={handleClose} className="flex-1 h-[var(--btn-h)] inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Aceptar</button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default function UICatalog() {
  const [showModalOld, setShowModalOld] = useState(false);
  const [showModalNew, setShowModalNew] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [selectedCardNew, setSelectedCardNew] = useState<string | null>(null);
  const [selectValue, setSelectValue] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetSelection, setSheetSelection] = useState('normal');

  return (
    <div className="min-h-screen bg-surface-bg overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface-card/80 backdrop-blur-md border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-[var(--page-px-mobile)] md:px-[var(--page-px-desktop)] py-4 flex items-center justify-between">
          <div>
            <h1 className="text-[length:var(--text-page-title-mobile)] md:text-[length:var(--text-page-title-desktop)] font-semibold text-neutral-900">Atlas Design System</h1>
            <p className="text-[length:var(--text-body)] text-neutral-400">Antes → Después · Plus Jakarta Sans · Tokens CSS</p>
          </div>
          <a href="/" className="text-[length:var(--text-body)] text-neutral-600 hover:text-neutral-900 flex items-center gap-1 font-medium">
            <ArrowLeft className="w-4 h-4" /> Volver
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-[var(--page-px-mobile)] md:px-[var(--page-px-desktop)] py-[var(--page-py-mobile)] md:py-[var(--page-py-desktop)]">

        {/* ===== 0. DESIGN TOKENS ===== */}
        <SectionTitle>0. Design Tokens</SectionTitle>

        <SubSection title="Colores de marca">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { name: 'brand-primary', hex: '#1644EB', cls: 'bg-brand-primary' },
              { name: 'brand-hover', hex: '#1238C8', cls: 'bg-brand-primary-hover' },
              { name: 'brand-deep', hex: '#01269B', cls: 'bg-brand-deep' },
              { name: 'brand-black', hex: '#02062B', cls: 'bg-brand-black' },
              { name: 'brand-accent', hex: '#60E7F0', cls: 'bg-brand-accent' },
              { name: 'accent-soft', hex: '#D6F8FB', cls: 'bg-brand-accent-soft' },
            ].map((c) => (
              <div key={c.name} className="rounded-lg border border-neutral-200 p-3">
                <div className={`${c.cls} w-full h-12 rounded-md mb-2`} />
                <p className="text-[length:var(--text-caption)] font-medium text-neutral-900">{c.name}</p>
                <p className="text-[length:var(--text-caption)] text-neutral-400 font-mono">{c.hex}</p>
              </div>
            ))}
          </div>
        </SubSection>

        <SubSection title="Superficies (oscuro → claro: bg → card → elevated)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { name: 'surface-bg', hex: '#F0F2F7', cls: 'bg-surface-bg', usage: 'Fondo principal (más oscuro)' },
              { name: 'surface-card', hex: '#FFFFFF', cls: 'bg-surface-card', usage: 'Cards, modales (intermedio)' },
              { name: 'surface-elevated', hex: '#F7F8FB', cls: 'bg-surface-elevated', usage: 'Hover, inputs (más claro)' },
            ].map((c) => (
              <div key={c.name} className="rounded-lg border border-neutral-200 p-3">
                <div className={`${c.cls} w-full h-12 rounded-md mb-2 border border-neutral-200`} />
                <p className="text-[length:var(--text-caption)] font-medium text-neutral-900">{c.name}</p>
                <p className="text-[length:var(--text-caption)] text-neutral-400 font-mono">{c.hex}</p>
                <p className="text-[length:var(--text-caption)] text-neutral-600 mt-0.5">{c.usage}</p>
              </div>
            ))}
          </div>
        </SubSection>

        <SubSection title="Neutrales">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { name: '900', hex: '#0F1729', cls: 'bg-neutral-900' },
              { name: '700', hex: '#334155', cls: 'bg-neutral-700' },
              { name: '600', hex: '#4A5568', cls: 'bg-neutral-600' },
              { name: '400', hex: '#94A3B8', cls: 'bg-neutral-400' },
              { name: '200', hex: '#E2E8F0', cls: 'bg-neutral-200' },
              { name: '100', hex: '#F1F5F9', cls: 'bg-neutral-100' },
            ].map((c) => (
              <div key={c.name} className="rounded-lg border border-neutral-200 p-3">
                <div className={`${c.cls} w-full h-10 rounded-md mb-2`} />
                <p className="text-[length:var(--text-caption)] font-medium text-neutral-900">neutral-{c.name}</p>
                <p className="text-[length:var(--text-caption)] text-neutral-400 font-mono">{c.hex}</p>
              </div>
            ))}
          </div>
        </SubSection>

        <SubSection title="Estados semánticos (color + soft + text)">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { name: 'success', cls: 'bg-success', soft: 'bg-success-soft', textCls: 'text-success-text', textHex: '#065F46' },
              { name: 'warning', cls: 'bg-warning', soft: 'bg-warning-soft', textCls: 'text-warning-text', textHex: '#92400E' },
              { name: 'error', cls: 'bg-error', soft: 'bg-error-soft', textCls: 'text-error-text', textHex: '#991B1B' },
              { name: 'info', cls: 'bg-info', soft: 'bg-info-soft', textCls: 'text-info-text', textHex: '#1E40AF' },
            ].map((c) => (
              <div key={c.name} className="rounded-lg border border-neutral-200 p-3">
                <div className="flex gap-2 mb-2">
                  <div className={`${c.cls} flex-1 h-8 rounded-md`} />
                  <div className={`${c.soft} flex-1 h-8 rounded-md border border-neutral-200 flex items-center justify-center`}>
                    <span className={`${c.textCls} text-[11px] font-semibold`}>Aa</span>
                  </div>
                </div>
                <p className="text-[length:var(--text-caption)] font-medium text-neutral-900">{c.name}</p>
                <p className="text-[length:var(--text-caption)] text-neutral-400 font-mono">text: {c.textHex}</p>
              </div>
            ))}
          </div>
        </SubSection>

        <SubSection title="Radius — jerarquía de uso">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
            <p className="text-xs font-medium text-amber-800">⚠ Regla concéntrica: <code>R_inner = R_outer − padding</code>. Elemento interno siempre menor que externo.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { name: 'rounded-sm', val: '8px', cls: 'rounded-sm', use: 'Elementos internos: icon containers, badges, chips, indicadores de tabs, elementos hijos dentro de rounded-md' },
              { name: 'rounded-md', val: '12px', cls: 'rounded-md', use: 'Componentes interactivos: botones, inputs, filas de lista, option cards, tab containers, dropdowns' },
              { name: 'rounded-lg', val: '16px', cls: 'rounded-lg', use: 'Contenedores principales: cards de página, modals, sheets/panels, secciones de contenido' },
              { name: 'rounded-full', val: '9999px', cls: 'rounded-full', use: 'Solo avatares circulares y pill badges. No usar en botones ni containers.' },
            ].map((r) => (
              <div key={r.name} className={`bg-surface-card ${r.cls} border border-neutral-200 p-4`}>
                <p className="text-sm font-semibold text-neutral-900">{r.name} <span className="font-normal text-neutral-400">({r.val})</span></p>
                <p className="text-xs text-neutral-600 mt-1">{r.use}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {[
              { name: 'shadow-sm', cls: 'shadow-sm' },
              { name: 'shadow-md', cls: 'shadow-md' },
              { name: 'shadow-lg', cls: 'shadow-lg' },
            ].map((s) => (
              <div key={s.name} className={`bg-surface-card rounded-lg border border-neutral-200 p-4 ${s.cls}`}>
                <p className="text-[length:var(--text-caption)] font-medium text-neutral-900">{s.name}</p>
              </div>
            ))}
          </div>
        </SubSection>

        {/* ===== 1. TIPOGRAFÍA ===== */}
        <SectionTitle>1. Tipografía</SectionTitle>

        <SubSection title="Escala tipográfica — Antes vs Después">
          <BeforeAfter
            labelBefore="6+ tamaños inconsistentes, system font"
            labelAfter="5 tamaños estandarizados, Plus Jakarta Sans"
            before={
              <div className="space-y-3" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                <div><CodeLabel>text-4xl font-semibold</CodeLabel><h1 className="text-4xl font-semibold text-gray-900">Welcome</h1></div>
                <div><CodeLabel>text-lg font-semibold</CodeLabel><h1 className="text-lg font-semibold text-gray-900">Settings</h1></div>
                <div><CodeLabel>text-sm font-semibold</CodeLabel><h1 className="text-sm font-semibold text-gray-900">Mobile header</h1></div>
                <div><CodeLabel>text-lg font-medium</CodeLabel><h3 className="text-lg font-medium text-gray-900">Modal title</h3></div>
                <div><CodeLabel>text-base font-semibold</CodeLabel><h3 className="text-base font-semibold text-gray-900">Card title</h3></div>
                <div><CodeLabel>text-base text-gray-800</CodeLabel><p className="text-base text-gray-800">Body text</p></div>
                <div><CodeLabel>text-sm text-gray-600</CodeLabel><p className="text-sm text-gray-600">Secondary text</p></div>
                <div><CodeLabel>text-xs text-gray-500</CodeLabel><p className="text-xs text-gray-500">Caption text</p></div>
              </div>
            }
            after={
              <div className="space-y-3">
                <div><CodeLabel>page-title: 20px/24px · semibold (600)</CodeLabel><h1 className="text-[length:var(--text-page-title-mobile)] md:text-[length:var(--text-page-title-desktop)] font-semibold text-neutral-900">Título de página</h1></div>
                <div><CodeLabel>section-title: 16px/18px · semibold (600)</CodeLabel><h2 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">Título de sección</h2></div>
                <div><CodeLabel>body: 14px · regular (400) · neutral-900</CodeLabel><p className="text-[length:var(--text-body)] text-neutral-900">Texto principal del cuerpo de la aplicación.</p></div>
                <div><CodeLabel>body-secondary: 14px · regular (400) · neutral-600</CodeLabel><p className="text-[length:var(--text-body)] text-neutral-600">Texto secundario, descripciones y subtítulos.</p></div>
                <div><CodeLabel>caption: 12px · medium (500) · neutral-400</CodeLabel><p className="text-[length:var(--text-caption)] font-medium text-neutral-400">Labels, badges, hints y texto auxiliar.</p></div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 2. BOTONES ===== */}
        <SectionTitle>2. Botones</SectionTitle>

        <SubSection title="8 variantes → 4 estandarizadas">
          <BeforeAfter
            labelBefore="8 variantes: pill, rect, grande, sec pill, sec rect, ghost, icon circle, menu"
            labelAfter="4 variantes: Primary, Secondary, Ghost, Icon"
            before={
              <div className="space-y-3" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                <div className="flex flex-wrap gap-2 items-center">
                  <button className="h-10 px-4 inline-flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm text-sm">Pill</button>
                  <button className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">Rect</button>
                  <button className="px-8 py-4 bg-blue-500 text-white rounded-full shadow-sm">Grande</button>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 shadow-sm text-sm">Sec Pill</button>
                  <button className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">Sec Rect</button>
                  <button className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Ghost</button>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button className="w-10 h-10 rounded-full bg-gray-100 inline-flex items-center justify-center text-gray-700"><ChevronLeft className="w-5 h-5" /></button>
                  <button className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-gray-300 text-gray-700 shadow-sm text-sm"><Menu size={18} />Menú</button>
                </div>
              </div>
            }
            after={
              <div className="space-y-4">
                <div>
                  <CodeLabel>Primary — bg-brand-primary h-10 px-5 rounded-md font-medium</CodeLabel>
                  <div className="flex flex-wrap gap-2 items-center mt-2">
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      Continuar <ChevronRight className="w-4 h-4" />
                    </button>
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      <Instagram className="w-4 h-4" /> Conectar
                    </button>
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-neutral-200 text-neutral-400 rounded-md font-medium text-[length:var(--text-body)] cursor-not-allowed">
                      Disabled
                    </button>
                  </div>
                </div>
                <div>
                  <CodeLabel>Secondary — bg-surface-card border-neutral-200 h-10 px-5 rounded-md</CodeLabel>
                  <div className="flex flex-wrap gap-2 items-center mt-2">
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      Cancelar
                    </button>
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      <Download className="w-4 h-4" /> Descargar
                    </button>
                    <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      <Copy className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                </div>
                <div>
                  <CodeLabel>Ghost — text-neutral-600 h-10 px-4 rounded-md hover:bg-neutral-100</CodeLabel>
                  <div className="flex flex-wrap gap-2 items-center mt-2">
                    <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center text-neutral-600 hover:bg-neutral-100 rounded-md font-medium text-[length:var(--text-body)] transition-colors">
                      Volver
                    </button>
                    <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center text-brand-primary hover:bg-neutral-100 rounded-md font-medium text-[length:var(--text-body)] transition-colors">
                      Preguntas frecuentes
                    </button>
                  </div>
                </div>
                <div>
                  <CodeLabel>Icon — w-10 h-10 rounded-md bg-surface-card border shadow-sm</CodeLabel>
                  <div className="flex flex-wrap gap-2 items-center mt-2">
                    <button className="w-[var(--btn-h)] h-[var(--btn-h)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 border border-neutral-200 rounded-md text-neutral-900 shadow-sm transition-colors">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button className="w-[var(--btn-h)] h-[var(--btn-h)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 border border-neutral-200 rounded-md text-neutral-900 shadow-sm transition-colors">
                      <Star className="w-4 h-4" />
                    </button>
                    <button className="w-[var(--btn-h)] h-[var(--btn-h)] inline-flex items-center justify-center bg-surface-card hover:bg-error-soft border border-neutral-200 rounded-md text-error shadow-sm transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 border border-neutral-200 rounded-md text-neutral-900 font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                      <Menu className="w-4 h-4" /> Menú
                    </button>
                  </div>
                </div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 3. LISTAS (shadcn style) ===== */}
        <SectionTitle>3. Listas de Elementos</SectionTitle>

        <SubSection title="Toolbar horizontal — botones en fila">
          <NewCard>
            <div className="flex flex-wrap items-center gap-2">
              <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                <Plus className="w-4 h-4" /> Nuevo
              </button>
              <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                <Filter className="w-4 h-4" /> Filtrar
              </button>
              <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                <Calendar className="w-4 h-4" /> Fecha
              </button>
              <div className="hidden sm:block flex-1" />
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input type="text" placeholder="Buscar..." className="h-[var(--btn-h)] pl-10 pr-3 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary outline-none bg-surface-card w-full sm:w-48" />
              </div>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Tabs / Segmented control">
          <NewCard>
            <div className="flex items-center gap-1 p-1 bg-neutral-100 rounded-md w-fit">
              <button className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-surface-card text-neutral-900 rounded-sm font-medium text-[length:var(--text-caption)] shadow-sm transition-colors">
                <FileText className="w-3.5 h-3.5" /> Todos
              </button>
              <button className="h-8 px-3 inline-flex items-center justify-center gap-1.5 text-neutral-600 rounded-sm font-medium text-[length:var(--text-caption)] hover:text-neutral-900 transition-colors">
                <Image className="w-3.5 h-3.5" /> Imágenes
              </button>
              <button className="h-8 px-3 inline-flex items-center justify-center gap-1.5 text-neutral-600 rounded-sm font-medium text-[length:var(--text-caption)] hover:text-neutral-900 transition-colors">
                <Star className="w-3.5 h-3.5" /> Favoritos
              </button>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Lista vertical — navegación / settings">
          <NewCard>
            <div className="divide-y divide-neutral-200 -mx-[var(--card-padding-mobile)] md:-mx-[var(--card-padding-desktop)]">
              {[
                { icon: User, label: 'Perfil', desc: 'Nombre, email y foto de perfil' },
                { icon: Bell, label: 'Notificaciones', desc: 'Preferencias de alertas y emails' },
                { icon: Settings, label: 'Configuración', desc: 'Idioma, tema y preferencias' },
                { icon: Mail, label: 'Integraciones', desc: 'Conectar servicios externos' },
              ].map((item, i) => (
                <button key={i} className="w-full flex items-center gap-4 px-[var(--card-padding-mobile)] md:px-[var(--card-padding-desktop)] py-3 hover:bg-neutral-100 transition-colors text-left">
                  <div className="w-9 h-9 rounded-md bg-neutral-100 flex items-center justify-center text-neutral-600 shrink-0">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[length:var(--text-body)] font-medium text-neutral-900">{item.label}</p>
                    <p className="text-[length:var(--text-caption)] text-neutral-400">{item.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                </button>
              ))}
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Lista vertical — items con acciones y badges">
          <NewCard>
            <div className="space-y-1">
              {[
                { name: 'Campaña de verano', status: 'Activo', statusCls: 'bg-success-soft text-success-text' },
                { name: 'Lanzamiento producto', status: 'Borrador', statusCls: 'bg-neutral-100 text-neutral-600' },
                { name: 'Promo fin de año', status: 'Pausado', statusCls: 'bg-warning-soft text-warning-text' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-md hover:bg-neutral-100 transition-colors">
                  <div className="w-9 h-9 rounded-md bg-neutral-100 flex items-center justify-center text-neutral-600 shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[length:var(--text-body)] font-medium text-neutral-900 truncate">{item.name}</p>
                  </div>
                  <span className={`text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm ${item.statusCls}`}>{item.status}</span>
                  <button className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-200 transition-colors">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Grid de acciones — botones verticales">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Instagram, label: 'Instagram', desc: 'Publicar contenido' },
              { icon: Image, label: 'Generar imagen', desc: 'Crear con IA' },
              { icon: FileText, label: 'Descripción', desc: 'Texto para redes' },
              { icon: Calendar, label: 'Programar', desc: 'Agendar publicación' },
            ].map((item, i) => (
              <button key={i} className="flex flex-col items-center gap-2 p-4 rounded-lg border border-neutral-200 bg-surface-card hover:bg-neutral-100 transition-colors text-center">
                <div className="w-10 h-10 rounded-md bg-neutral-100 flex items-center justify-center text-neutral-600">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[length:var(--text-body)] font-medium text-neutral-900">{item.label}</p>
                  <p className="text-[length:var(--text-caption)] text-neutral-400">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </SubSection>

        <SubSection title="Icon container — fila con icono (default / selected)">
          <NewCard>
            <p className="text-[length:var(--text-caption)] font-medium text-neutral-400 mb-3">
              Contenedor: <code className="text-neutral-600">w-9 h-9 rounded-sm flex items-center justify-center shrink-0</code> · Icono: <code className="text-neutral-600">w-4 h-4</code> · Gap padre: <code className="text-neutral-600">gap-3</code>
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              <p className="text-xs font-medium text-amber-800">⚠ Regla de radios concéntricos (nunca romper): <code>R_inner = R_outer − padding</code>. Fila <code>rounded-md</code> (12px) con <code>px-3 py-3</code> (12px) → icono <code>rounded-sm</code> (8px) o menor.</p>
            </div>
            <div className="space-y-1">
              {/* Selected row */}
              <div className="flex items-center gap-3 px-3 py-3 rounded-md bg-neutral-100">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-brand-primary text-white">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900">Opción seleccionada</p>
                  <p className="text-xs text-neutral-600">Descripción de la opción activa.</p>
                </div>
                <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />
              </div>
              {/* Default row */}
              <div className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-neutral-100 transition-colors cursor-pointer">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
                  <Settings className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900">Opción no seleccionada</p>
                  <p className="text-xs text-neutral-400">Descripción inactiva.</p>
                </div>
              </div>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Negro Atlas — text-brand-black reemplaza text-black">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
            <p className="text-xs font-medium text-amber-800">⚠ Regla: Nunca usar <code>text-black</code> para textos. Siempre usar <code>text-brand-black</code> (#02062B). Los <code>bg-black</code>, <code>border-black</code> y overlays se mantienen.</p>
          </div>
          <NewCard>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <CodeLabel>text-brand-black/90 — Texto principal</CodeLabel>
                  <p className="text-[15px] text-brand-black/90 mt-1">Buenos días, Matías.</p>
                </div>
                <div className="flex-1">
                  <CodeLabel>text-brand-black/80 — Texto cuerpo</CodeLabel>
                  <p className="text-[15px] text-brand-black/80 mt-1">¿Te está gustando Atlas?</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <CodeLabel>text-brand-black/60 — Secundario/acción</CodeLabel>
                  <p className="text-[15px] text-brand-black/60 mt-1">Cancelar</p>
                </div>
                <div className="flex-1">
                  <CodeLabel>text-brand-black/50 — Tab inactivo</CodeLabel>
                  <p className="text-[13px] font-medium text-brand-black/50 mt-1">Personalizado</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <CodeLabel>text-brand-black/40 — Icono close (desktop)</CodeLabel>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/[0.06] text-brand-black/40 mt-1"><X className="w-4 h-4" /></span>
                </div>
                <div className="flex-1">
                  <CodeLabel>text-[#3C3C43]/20 — Botón disabled</CodeLabel>
                  <span className="text-[15px] font-semibold text-[#3C3C43]/20 mt-1">Aceptar</span>
                </div>
              </div>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Apple HIG List Row — Material Symbols Rounded (filled)">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
            <p className="text-xs font-medium text-amber-800">Iconos: <code>material-symbols-rounded</code> con <code>FILL=1</code> (default). Tamaño en listas: <code>text-[20px]</code>. Color icono: <code>text-[#1644EB]</code>. Texto: <code>text-brand-black/90</code>.</p>
          </div>
          <NewCard>
            <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">tune</span>
                  Calidad
                </span>
                <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                  Normal
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
              <div className="ml-[46px] h-px bg-black/[0.08]" />
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">grid_view</span>
                  Formato
                </span>
                <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                  Posteo
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
            </div>
            <div className="mt-4 rounded-[13px] bg-white overflow-hidden">
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">auto_awesome</span>
                  Automático
                </span>
                <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
              </button>
              <div className="ml-[46px] h-px bg-black/[0.08]" />
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">brush</span>
                  Personalizado
                </span>
                <span className="flex items-center gap-1">
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
              <div className="ml-[46px] h-px bg-black/[0.08]" />
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0" style={{ fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20" }}>square</span>
                  Fondo blanco
                </span>
              </button>
              <div className="ml-[46px] h-px bg-black/[0.08]" />
              <button type="button" className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between hover:bg-black/[0.02]">
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">photo_library</span>
                  Biblioteca
                </span>
                <span className="flex items-center gap-1">
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Mobile Sheet Header — Cancelar / Título / Aceptar">
          <NewCard>
            <p className="text-[length:var(--text-caption)] font-medium text-neutral-400 mb-3">
              Título: <code className="text-neutral-600">text-[15px] font-semibold text-brand-black/90</code> · Cancelar: <code className="text-neutral-600">text-[#1644EB]</code> · Aceptar activo: <code className="text-neutral-600">text-[#1644EB] font-semibold</code> · Aceptar disabled: <code className="text-neutral-600">text-[#3C3C43]/20</code>
            </p>
            <div className="rounded-[13px] bg-[#F2F2F7] overflow-hidden">
              <div className="py-2"><div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" /></div>
              <div className="flex items-center justify-between px-4 pb-3">
                <button type="button" className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                <h3 className="text-[15px] font-semibold text-brand-black/90">Calidad de imagen</h3>
                <span className="text-[15px] font-semibold text-[#1644EB] w-20 text-right">Aceptar</span>
              </div>
            </div>
            <div className="mt-3 rounded-[13px] bg-[#F2F2F7] overflow-hidden">
              <div className="py-2"><div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" /></div>
              <div className="flex items-center justify-between px-4 pb-3">
                <button type="button" className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                <h3 className="text-[15px] font-semibold text-brand-black/90">Biblioteca de escenas</h3>
                <span className="text-[15px] font-semibold text-[#3C3C43]/20 w-20 text-right cursor-not-allowed">Aceptar</span>
              </div>
            </div>
          </NewCard>
        </SubSection>

        {/* ===== 4. CARDS ===== */}
        <SectionTitle>4. Cards / Contenedores</SectionTitle>

        <SubSection title="Page container">
          <BeforeAfter
            labelBefore="rounded-2xl, shadow-sm, p-6, border-gray-100"
            labelAfter="rounded-lg, border only (no shadow on bg), p-[card-padding]"
            before={
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <h1 className="text-lg font-semibold text-gray-900">Título de página</h1>
                <p className="text-sm text-gray-600 mt-1">Subtítulo descriptivo</p>
                <div className="mt-4 h-16 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 text-sm">Contenido</div>
              </div>
            }
            after={
              <div className="bg-surface-card rounded-lg p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] border border-neutral-200">
                <h1 className="text-[length:var(--text-page-title-mobile)] md:text-[length:var(--text-page-title-desktop)] font-semibold text-neutral-900">Título de página</h1>
                <p className="text-[length:var(--text-body)] text-neutral-600 mt-1">Subtítulo descriptivo</p>
                <div className="mt-4 h-16 bg-neutral-100 rounded-md flex items-center justify-center text-neutral-400 text-[length:var(--text-body)]">Contenido</div>
              </div>
            }
          />
        </SubSection>

        <SubSection title="Selection Card">
          <BeforeAfter
            labelBefore="rounded-2xl, bg-blue-50 icon + blue-600 icon (azul sobre celeste)"
            labelAfter="rounded-lg, bg-neutral-100 icon + neutral-600 (sin azul sobre celeste)"
            before={
              <div className="grid grid-cols-1 gap-3" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <button type="button" onClick={() => setSelectedCard(selectedCard === 'old-a' ? null : 'old-a')} className={`w-full text-left border rounded-2xl p-4 flex items-start gap-4 transition-colors ${selectedCard === 'old-a' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className="shrink-0 bg-blue-50 p-2 rounded-md"><Zap className="w-6 h-6 text-blue-600" /></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-semibold text-gray-900">Opción A</h2>
                      {selectedCard === 'old-a' && <span className="w-6 h-6 rounded-full bg-blue-500 text-white inline-flex items-center justify-center"><Check className="w-4 h-4" /></span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Descripción de la opción.</p>
                  </div>
                </button>
              </div>
            }
            after={
              <div className="grid grid-cols-1 gap-3">
                <button type="button" onClick={() => setSelectedCardNew(selectedCardNew === 'new-a' ? null : 'new-a')} className={`w-full text-left border rounded-lg p-[var(--card-padding-mobile)] flex items-start gap-4 transition-colors ${selectedCardNew === 'new-a' ? 'border-brand-primary ring-2 ring-brand-primary' : 'border-neutral-200 hover:bg-neutral-100'}`}>
                  <div className="shrink-0 w-10 h-10 bg-neutral-100 rounded-md flex items-center justify-center text-neutral-600"><Zap className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h2 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">Opción A</h2>
                      {selectedCardNew === 'new-a' && <span className="w-6 h-6 rounded-md bg-brand-primary text-white inline-flex items-center justify-center"><Check className="w-4 h-4" /></span>}
                    </div>
                    <p className="text-[length:var(--text-body)] text-neutral-600 mt-1">Descripción de la opción.</p>
                  </div>
                </button>
              </div>
            }
          />
        </SubSection>

        <SubSection title="Cards con imagen — Selección de producto">
          <div className="-mx-[var(--page-px-mobile)] sm:mx-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 px-[var(--page-px-mobile)] sm:px-0">
              {[
                { id: 'rem', name: 'Remera Oversize', sku: 'REM-001', color: 'bg-gradient-to-br from-sky-100 to-sky-200' },
                { id: 'hoo', name: 'Hoodie Premium', sku: 'HOO-042', color: 'bg-gradient-to-br from-violet-100 to-violet-200' },
                { id: 'gor', name: 'Gorra Snapback', sku: 'GOR-018', color: 'bg-gradient-to-br from-amber-100 to-amber-200' },
                { id: 'pan', name: 'Pantalón Cargo', sku: 'PAN-007', color: 'bg-gradient-to-br from-emerald-100 to-emerald-200' },
                { id: 'zap', name: 'Zapatillas Runner', sku: 'ZAP-033', color: 'bg-gradient-to-br from-rose-100 to-rose-200' },
                { id: 'moc', name: 'Mochila Urban', sku: 'MOC-055', color: 'bg-gradient-to-br from-slate-100 to-slate-200' },
              ].map((product) => {
                const isSelected = selectedProduct === product.id;
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => setSelectedProduct(isSelected ? null : product.id)}
                    className={`group text-left rounded-lg overflow-hidden transition-all ${
                      isSelected
                        ? 'ring-2 ring-brand-primary border border-brand-primary shadow-sm'
                        : 'border border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div className={`${product.color} aspect-square flex items-center justify-center relative`}>
                      <ShoppingBag className="w-8 h-8 text-neutral-400/60" />
                      <span className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-brand-primary text-white shadow-sm'
                          : 'border-2 border-neutral-300 bg-white/70'
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </span>
                    </div>
                    <div className="p-3">
                      <p className="text-[length:var(--text-body)] font-medium text-neutral-900 truncate">{product.name}</p>
                      <p className="text-[length:var(--text-caption)] text-neutral-400 mt-0.5">{product.sku}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </SubSection>

        <SubSection title="Cards con imagen — Historial de imágenes generadas">
          <div className="-mx-[var(--page-px-mobile)] sm:mx-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 px-[var(--page-px-mobile)] sm:px-0">
              {[
                { label: 'Campaña Verano', date: 'Hace 2 horas', color: 'bg-gradient-to-br from-orange-100 to-rose-200', fav: true },
                { label: 'Story Instagram', date: 'Hace 5 horas', color: 'bg-gradient-to-br from-blue-100 to-indigo-200', fav: false },
                { label: 'Banner Web', date: 'Ayer', color: 'bg-gradient-to-br from-teal-100 to-cyan-200', fav: true },
                { label: 'Post Facebook', date: 'Hace 2 días', color: 'bg-gradient-to-br from-pink-100 to-fuchsia-200', fav: false },
              ].map((img, i) => (
                <div key={i} className="group relative rounded-lg overflow-hidden cursor-pointer">
                  <div className={`${img.color} aspect-[4/5] flex items-center justify-center relative`}>
                    <Image className="w-8 h-8 text-neutral-400/60" />
                    {img.fav && (
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-red-500">
                        <Heart className="w-3 h-3 fill-current" />
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 flex items-end gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[length:var(--text-body)] font-medium text-white truncate">{img.label}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3 text-white/60" />
                          <p className="text-[length:var(--text-caption)] text-white/60">{img.date}</p>
                        </div>
                      </div>
                      <button type="button" className="w-7 h-7 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors shrink-0">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SubSection>

        <SubSection title="Card con imagen — Idea para vista de detalle (futuro)">
          <NewCard>
            <div className="max-w-sm">
              <div className="border border-neutral-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-br from-violet-100 to-indigo-200 aspect-video flex items-center justify-center relative">
                  <Image className="w-10 h-10 text-neutral-400/60" />
                  <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider bg-brand-primary text-white px-2 py-0.5 rounded-sm">Pro</span>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <h4 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">Campaña Primavera 2025</h4>
                    <p className="text-[length:var(--text-body)] text-neutral-400 mt-1">Generada para Instagram Stories con calidad Pro.</p>
                  </div>
                  <div className="flex items-center gap-2 text-[length:var(--text-caption)] text-neutral-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Hace 3 horas</span>
                    <span className="text-neutral-200">·</span>
                    <Package className="w-3.5 h-3.5" />
                    <span>Hoodie Premium</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-neutral-200">
                    <button type="button" className="h-[var(--btn-h-sm)] px-3 inline-flex items-center justify-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-caption)] shadow-sm transition-colors">
                      <Download className="w-3.5 h-3.5" /> Descargar
                    </button>
                    <button type="button" className="h-[var(--btn-h-sm)] px-3 inline-flex items-center justify-center gap-1.5 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-caption)] shadow-sm transition-colors">
                      <Share2 className="w-3.5 h-3.5" /> Compartir
                    </button>
                    <button type="button" className="h-[var(--btn-h-sm)] w-[var(--btn-h-sm)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-400 border border-neutral-200 rounded-md shadow-sm transition-colors ml-auto">
                      <Heart className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" className="h-[var(--btn-h-sm)] w-[var(--btn-h-sm)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-400 border border-neutral-200 rounded-md shadow-sm transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </NewCard>
        </SubSection>

        {/* ===== 5. INPUTS ===== */}
        <SectionTitle>5. Inputs</SectionTitle>

        <SubSection title="Text, Textarea, Custom Select, Search">
          <BeforeAfter
            labelBefore="3 border-radius, border-gray-300, native select del sistema"
            labelAfter="rounded-md uniforme, custom dropdown, border-neutral-200"
            before={
              <div className="space-y-3 max-w-sm" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <input type="text" placeholder="rounded-lg (ProductEdit)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <input type="text" placeholder="rounded-md (Settings)" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" placeholder="rounded-full (Search)" className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                  <option>Native select (sistema)</option>
                  <option>Opción 1</option>
                </select>
              </div>
            }
            after={
              <div className="space-y-3 max-w-sm">
                <div>
                  <label className="text-[length:var(--text-caption)] font-medium text-neutral-600 mb-1 block">Nombre del producto</label>
                  <input type="text" placeholder="Escribí el nombre..." className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card" />
                </div>
                <div>
                  <label className="text-[length:var(--text-caption)] font-medium text-neutral-600 mb-1 block">Descripción</label>
                  <textarea placeholder="Describí el producto..." rows={2} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card resize-none" />
                </div>
                <div>
                  <label className="text-[length:var(--text-caption)] font-medium text-neutral-600 mb-1 block">Buscar</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input type="text" placeholder="Buscar productos..." className="w-full pl-10 pr-3 py-2.5 border border-neutral-200 rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card" />
                  </div>
                </div>
                <div>
                  <label className="text-[length:var(--text-caption)] font-medium text-neutral-600 mb-1 block">Tipo (custom dropdown)</label>
                  <CustomSelect
                    placeholder="Seleccionar tipo..."
                    value={selectValue}
                    onChange={setSelectValue}
                    options={[
                      { value: 'producto', label: 'Producto' },
                      { value: 'servicio', label: 'Servicio' },
                      { value: 'combo', label: 'Combo' },
                    ]}
                  />
                </div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 6. MODALES ===== */}
        <SectionTitle>6. Modales</SectionTitle>

        <SubSection title="Modal — Antes vs Después">
          <BeforeAfter
            labelBefore="rounded-lg, font-medium title, bg-black/50 overlay"
            labelAfter="rounded-lg (16px), font-semibold title, bg-black/50 + backdrop-blur overlay"
            before={
              <button onClick={() => setShowModalOld(true)} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm text-sm" style={{ fontFamily: 'system-ui, sans-serif' }}>
                Abrir modal antiguo
              </button>
            }
            after={
              <button onClick={() => setShowModalNew(true)} className="h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                Abrir modal nuevo
              </button>
            }
          />
        </SubSection>

        {showModalOld && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Modal Antiguo</h3>
                <button onClick={() => setShowModalOld(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="mb-6">
                <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Este es el modal con los estilos anteriores.</p>
                <p className="text-sm text-gray-500">rounded-lg, font-medium, blue-500 buttons.</p>
              </div>
              <div className="flex justify-end gap-4">
                <button onClick={() => setShowModalOld(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={() => setShowModalOld(false)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Proceder</button>
              </div>
            </div>
          </div>
        )}

        {showModalNew && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
            <div className="bg-surface-card rounded-lg p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] max-w-md w-full animate-modal-appear shadow-lg border border-neutral-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">Modal Nuevo</h3>
                <button onClick={() => setShowModalNew(false)} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="mb-6">
                <AlertCircle className="w-8 h-8 text-warning mx-auto mb-4" />
                <p className="text-[length:var(--text-body)] text-neutral-900 mb-2">Este es el modal con los nuevos tokens de diseño.</p>
                <p className="text-[length:var(--text-body)] text-neutral-600">rounded-lg (16px), brand-primary, shadow-lg, Plus Jakarta Sans.</p>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowModalNew(false)} className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Cancelar</button>
                <button onClick={() => setShowModalNew(false)} className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Confirmar</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== 7. BADGES ===== */}
        <SectionTitle>7. Badges / Tags</SectionTitle>

        <SubSection title="Badges con contraste corregido">
          <BeforeAfter
            labelBefore="Colores directos (ej: warning #F59E0B sobre #FEF3C7 = bajo contraste)"
            labelAfter="Colores -text oscuros (ej: warning-text #92400E sobre #FEF3C7 = alto contraste)"
            before={
              <div className="flex flex-wrap gap-2 items-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-md">Principal</span>
                <span className="bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded-md font-medium">Beta</span>
                <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-md font-medium">Activo</span>
                <span className="bg-red-50 text-red-700 text-xs px-2 py-1 rounded-md font-medium">Error</span>
                <span className="bg-yellow-50 text-yellow-700 text-xs px-2 py-1 rounded-md font-medium">Pendiente</span>
                <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-md font-medium">Inactivo</span>
              </div>
            }
            after={
              <div className="flex flex-wrap gap-2 items-center">
                <span className="bg-brand-primary text-white text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm">Principal</span>
                <span className="bg-neutral-100 text-neutral-900 text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm border border-neutral-200">Beta</span>
                <span className="bg-success-soft text-success-text text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm">Activo</span>
                <span className="bg-error-soft text-error-text text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm">Error</span>
                <span className="bg-warning-soft text-warning-text text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm">Pendiente</span>
                <span className="bg-neutral-100 text-neutral-600 text-[length:var(--text-caption)] font-medium px-2.5 py-1 rounded-sm">Inactivo</span>
                <span className="w-6 h-6 rounded-md bg-brand-primary text-white inline-flex items-center justify-center"><Check className="w-4 h-4" /></span>
              </div>
            }
          />
        </SubSection>

        {/* ===== 8. PROGRESS BARS ===== */}
        <SectionTitle>8. Progress Bars</SectionTitle>

        <SubSection title="Barra de progreso">
          <BeforeAfter
            labelBefore="h-1 bg-gray-200 → bg-blue-500"
            labelAfter="h-1.5 bg-neutral-200 → bg-brand-primary rounded-full"
            before={
              <div className="space-y-3 max-w-sm" style={{ fontFamily: 'system-ui, sans-serif' }}>
                {[25, 50, 75, 100].map((p) => (
                  <div key={p}><CodeLabel>{p}%</CodeLabel><div className="w-full h-1 bg-gray-200 rounded-full mt-1"><div className="h-1 bg-blue-500 rounded-full" style={{ width: `${p}%` }} /></div></div>
                ))}
              </div>
            }
            after={
              <div className="space-y-3 max-w-sm">
                {[25, 50, 75, 100].map((p) => (
                  <div key={p}><CodeLabel>{p}%</CodeLabel><div className="w-full h-1.5 bg-neutral-200 rounded-full mt-1"><div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${p}%`, background: 'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)' }} /></div></div>
                ))}
              </div>
            }
          />
        </SubSection>

        {/* ===== 9. BOTTOM BARS ===== */}
        <SectionTitle>9. Bottom Bars (Mobile)</SectionTitle>

        <SubSection title="Bottom bar estandarizada">
          <BeforeAfter
            labelBefore="3 variantes distintas con diferentes paddings y estilos"
            labelAfter="1 variante unificada: border-t, p-3, botones Ghost + Primary"
            before={
              <div className="space-y-3" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <div className="bg-white border-t border-gray-200 p-3 rounded-b-lg">
                  <div className="flex items-center justify-between">
                    <button className="px-3 py-2 text-sm text-gray-700 rounded-md">Volver</button>
                    <button className="px-4 h-10 rounded-full bg-blue-500 text-white text-sm inline-flex items-center gap-1">Continuar <ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="bg-white px-4 py-4 rounded-b-lg">
                  <button className="w-full h-12 bg-blue-500 text-white rounded-full">Crear con Atlas</button>
                </div>
              </div>
            }
            after={
              <div className="space-y-3">
                <div className="bg-surface-card border-t border-neutral-200 p-3 rounded-b-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <button className="h-[var(--btn-h)] px-4 text-neutral-600 hover:bg-neutral-100 rounded-md font-medium text-[length:var(--text-body)] transition-colors">Volver</button>
                    <button className="h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm inline-flex items-center gap-2 transition-colors">Continuar <ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="bg-surface-card border-t border-neutral-200 p-3 rounded-b-lg shadow-sm">
                  <button className="w-full h-[var(--btn-h-lg)] bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">Crear con Atlas</button>
                </div>
                <div className="bg-surface-card border-t border-neutral-200 p-3 rounded-b-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <button className="flex-1 h-[var(--btn-h)] rounded-md font-medium flex items-center justify-center gap-2 px-3 bg-surface-card text-neutral-900 border border-neutral-200 hover:bg-neutral-100 text-[length:var(--text-body)] transition-colors"><Heart className="w-4 h-4" />Favorito</button>
                    <button className="flex-1 h-[var(--btn-h)] rounded-md font-medium flex items-center justify-center gap-2 px-3 bg-surface-card text-neutral-900 border border-neutral-200 hover:bg-neutral-100 text-[length:var(--text-body)] transition-colors"><Pencil className="w-4 h-4" />Editar</button>
                    <button className="flex-1 h-[var(--btn-h)] rounded-md font-medium flex items-center justify-center gap-2 px-3 bg-surface-card text-neutral-900 border border-neutral-200 hover:bg-neutral-100 text-[length:var(--text-body)] transition-colors"><Download className="w-4 h-4" />Descargar</button>
                  </div>
                </div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 10. ALERTS ===== */}
        <SectionTitle>10. Alerts / Banners</SectionTitle>

        <SubSection title="Banners de estado">
          <BeforeAfter
            labelBefore="Colores Tailwind directos, rounded-md"
            labelAfter="Tokens semánticos, rounded-sm (8px)"
            before={
              <div className="space-y-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm px-3 py-2 text-center">Info banner</div>
                <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 text-center">Error banner</div>
                <div className="rounded-md bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 text-center">Success banner</div>
              </div>
            }
            after={
              <div className="space-y-2">
                <div className="rounded-sm bg-info-soft border border-info/20 text-info-text text-[length:var(--text-body)] font-medium px-3 py-2.5 text-center">Info: Servidores en mantenimiento.</div>
                <div className="rounded-sm bg-error-soft border border-error/20 text-error-text text-[length:var(--text-body)] font-medium px-3 py-2.5 text-center">Error: No se pudo procesar.</div>
                <div className="rounded-sm bg-success-soft border border-success/20 text-success-text text-[length:var(--text-body)] font-medium px-3 py-2.5 text-center">Éxito: Operación completada.</div>
                <div className="rounded-sm bg-warning-soft border border-warning/20 text-warning-text text-[length:var(--text-body)] font-medium px-3 py-2.5 text-center">Advertencia: Revisá los datos.</div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 11. LOADING ===== */}
        <SectionTitle>11. Loading States</SectionTitle>

        <SubSection title="Spinner estandarizado">
          <BeforeAfter
            labelBefore="3 variantes de spinner distintas"
            labelAfter="1 spinner unificado: border-brand-primary"
            before={
              <div className="flex flex-wrap gap-6 items-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
                <div className="text-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /><CodeLabel>border spinner</CodeLabel></div>
                <div className="text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" /><CodeLabel>Loader2</CodeLabel></div>
                <div className="text-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto" /><CodeLabel>border-b</CodeLabel></div>
              </div>
            }
            after={
              <div className="flex flex-wrap gap-6 items-center">
                <div className="text-center"><div className="w-8 h-8 border-[3px] border-neutral-200 border-t-brand-primary rounded-full animate-spin mx-auto" /><CodeLabel>Large (page)</CodeLabel></div>
                <div className="text-center"><div className="w-5 h-5 border-2 border-neutral-200 border-t-brand-primary rounded-full animate-spin mx-auto" /><CodeLabel>Medium (inline)</CodeLabel></div>
                <div className="text-center"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto bg-brand-primary p-2 box-content rounded-md" /><CodeLabel>In button</CodeLabel></div>
              </div>
            }
          />
        </SubSection>

        {/* ===== 12. DROPDOWN MENUS ===== */}
        <SectionTitle>12. Dropdown Menus</SectionTitle>

        <SubSection title="Menú simple — solo opciones con icono">
          <NewCard>
            <div className="flex flex-wrap items-center gap-4">
              <DropdownMenu
                trigger={
                  <button className="w-[var(--btn-h)] h-[var(--btn-h)] inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 border border-neutral-200 rounded-md text-neutral-900 shadow-sm transition-colors">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                }
              >
                <DropdownItem icon={Eye} label="Ver detalle" />
                <DropdownItem icon={Pencil} label="Editar" />
                <DropdownItem icon={Copy} label="Duplicar" />
                <DropdownItem icon={Link} label="Copiar enlace" />
                <DropdownItem icon={Share2} label="Compartir" />
                <DropdownSeparator />
                <DropdownItem icon={Archive} label="Archivar" />
                <DropdownItem icon={Trash2} label="Eliminar" danger />
              </DropdownMenu>

              <DropdownMenu
                trigger={
                  <button className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                    Acciones <ChevronDown className="w-4 h-4 text-neutral-400" />
                  </button>
                }
              >
                <DropdownItem icon={Download} label="Descargar" />
                <DropdownItem icon={Star} label="Agregar a favoritos" />
                <DropdownItem icon={Flag} label="Reportar" />
                <DropdownSeparator />
                <DropdownItem icon={EyeOff} label="Ocultar" />
              </DropdownMenu>

              <p className="hidden sm:block text-[length:var(--text-caption)] text-neutral-400">← Hacé click en los botones para abrir</p>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Menú con iconos y descripciones">
          <NewCard>
            <div className="flex flex-wrap items-center gap-4">
              <DropdownMenu
                trigger={
                  <button className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors">
                    <Plus className="w-4 h-4" /> Crear nuevo <ChevronDown className="w-4 h-4" />
                  </button>
                }
              >
                <DropdownItem icon={Image} label="Imagen con IA" desc="Generá imágenes de producto con inteligencia artificial" />
                <DropdownItem icon={FileText} label="Descripción" desc="Creá textos optimizados para redes sociales" />
                <DropdownItem icon={Instagram} label="Publicación" desc="Diseñá y programá contenido para Instagram" />
                <DropdownSeparator />
                <DropdownItem icon={Sparkles} label="Campaña completa" desc="Generá imágenes, textos y programá todo junto" />
              </DropdownMenu>

              <p className="hidden sm:block text-[length:var(--text-caption)] text-neutral-400">← Menú con descripciones por opción</p>
            </div>
          </NewCard>
        </SubSection>

        {/* ===== 13. SHEET / SLIDING PANEL ===== */}
        <SectionTitle>13. Sheet / Panel Deslizable</SectionTitle>

        <SubSection title="Panel lateral (desktop) / inferior (mobile) con animaciones">
          <NewCard>
            <div className="space-y-3">
              <p className="text-[length:var(--text-body)] text-neutral-600">
                En <strong>desktop</strong> se desliza desde la derecha con un panel flotante redondeado.
                En <strong>mobile</strong> se desliza desde abajo con drag-to-dismiss.
                Igual que en PruebaNuevaInterfaz pero adaptado al nuevo design system.
              </p>
              <button
                onClick={() => setShowSheet(true)}
                className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                <Settings className="w-4 h-4" /> Abrir panel de configuración
              </button>
            </div>
          </NewCard>
        </SubSection>

        <Sheet open={showSheet} onClose={() => setShowSheet(false)} title="Calidad de imagen">
          <div className="space-y-0">
            <p className="text-[length:var(--text-body)] text-neutral-600 mb-4">
              Seleccioná la calidad de generación de imágenes para tu campaña.
            </p>
            <div className="space-y-1">
              {[
                { id: 'normal', icon: Image, name: 'Calidad normal', desc: 'Imágenes de buena calidad para uso general. Rápidas de generar.' },
                { id: 'pro', icon: Sparkles, name: 'Calidad Pro', desc: 'Imágenes ultra realistas con mayor detalle y resolución.' },
                { id: 'ultra', icon: Zap, name: 'Calidad Ultra', desc: 'Máxima calidad disponible. Ideal para impresión y publicidad premium.' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSheetSelection(opt.id)}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${
                    sheetSelection === opt.id ? 'bg-neutral-100' : ''
                  }`}
                >
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                    sheetSelection === opt.id ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}>
                    <opt.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[length:var(--text-body)] font-medium text-neutral-900">{opt.name}</p>
                    <p className={`text-[length:var(--text-caption)] ${sheetSelection === opt.id ? 'text-neutral-600' : 'text-neutral-400'}`}>{opt.desc}</p>
                  </div>
                  {sheetSelection === opt.id && (
                    <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />
                  )}
                </button>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-neutral-200">
              <h4 className="text-[length:var(--text-body)] font-medium text-neutral-900 mb-3">Opciones adicionales</h4>
              <div className="space-y-2">
                {[
                  { icon: Sparkles, label: 'Mejora automática', desc: 'Aplica correcciones de color y contraste' },
                  { icon: Image, label: 'Fondo transparente', desc: 'Genera la imagen sin fondo' },
                  { icon: Zap, label: 'Modo rápido', desc: 'Prioriza velocidad sobre calidad' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-md hover:bg-neutral-100 transition-colors">
                    <div className="w-9 h-9 rounded-md bg-neutral-100 flex items-center justify-center text-neutral-600 shrink-0">
                      <item.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[length:var(--text-body)] font-medium text-neutral-900">{item.label}</p>
                      <p className="text-[length:var(--text-caption)] text-neutral-400">{item.desc}</p>
                    </div>
                    <div className="w-10 h-6 rounded-full bg-neutral-200 relative cursor-pointer shrink-0 mt-0.5">
                      <div className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-surface-card shadow-sm transition-transform" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Sheet>

        {/* ===== 14. TOAST NOTIFICATIONS (Sileo) ===== */}
        <SectionTitle>14. Toast Notifications</SectionTitle>

        <SubSection title="Tipos de toast — sileo">
          <NewCard label="Librería: sileo · Gooey SVG morphing + spring physics">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => sileo.show({ title: 'Notificación por defecto', description: 'Este es un toast básico sin tipo específico.' })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                Default
              </button>
              <button
                onClick={() => sileo.success({ title: 'Guardado exitoso', description: 'Los cambios se guardaron correctamente.' })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-success/10 hover:bg-success/20 text-success-text border border-success/20 rounded-md font-medium text-[length:var(--text-body)] transition-colors"
              >
                <Check className="w-4 h-4" /> Success
              </button>
              <button
                onClick={() => sileo.error({ title: 'Error al guardar', description: 'No se pudo conectar con el servidor.' })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-error/10 hover:bg-error/20 text-error-text border border-error/20 rounded-md font-medium text-[length:var(--text-body)] transition-colors"
              >
                <AlertCircle className="w-4 h-4" /> Error
              </button>
              <button
                onClick={() => sileo.warning({ title: 'Atención', description: 'Tu plan está por vencer en 3 días.' })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-warning/10 hover:bg-warning/20 text-warning-text border border-warning/20 rounded-md font-medium text-[length:var(--text-body)] transition-colors"
              >
                <AlertCircle className="w-4 h-4" /> Warning
              </button>
              <button
                onClick={() => sileo.info({ title: 'Información', description: 'Tenés 5 imágenes disponibles para generar.' })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-info/10 hover:bg-info/20 text-info-text border border-info/20 rounded-md font-medium text-[length:var(--text-body)] transition-colors"
              >
                <Bell className="w-4 h-4" /> Info
              </button>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Toast con acción">
          <NewCard>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => sileo.success({ title: 'Imagen generada', description: 'Tu imagen está lista para descargar.', button: { title: 'Descargar', onClick: () => sileo.info({ title: 'Descargando...' }) } })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                <Download className="w-4 h-4" /> Toast con botón de acción
              </button>
              <button
                onClick={() => sileo.error({ title: 'Publicación eliminada', description: 'Se eliminó la publicación del calendario.', button: { title: 'Deshacer', onClick: () => sileo.success({ title: 'Restaurada', description: 'La publicación fue restaurada.' }) } })}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                <Trash2 className="w-4 h-4 text-neutral-400" /> Toast con deshacer
              </button>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Toast con promesa (loading → success / error)">
          <NewCard>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  const fakePromise = new Promise<{ name: string }>((resolve) => setTimeout(() => resolve({ name: 'Campaña de verano' }), 2000));
                  sileo.promise(fakePromise, {
                    loading: { title: 'Generando campaña...', description: 'Esto puede tomar unos segundos.' },
                    success: (data) => ({ title: `"${data.name}" creada`, description: 'Tu campaña está lista para publicar.' }),
                    error: () => ({ title: 'Error al generar', description: 'Intentá de nuevo más tarde.' }),
                  });
                }}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Promise → Success (2s)
              </button>
              <button
                onClick={() => {
                  const fakePromise = new Promise<void>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000));
                  sileo.promise(fakePromise, {
                    loading: { title: 'Subiendo imagen...', description: 'Procesando archivo...' },
                    success: () => ({ title: 'Imagen subida' }),
                    error: () => ({ title: 'Error al subir', description: 'El archivo excede el tamaño máximo.' }),
                  });
                }}
                className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-2 bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                <Loader2 className="w-4 h-4 text-neutral-400" /> Promise → Error (2s)
              </button>
            </div>
          </NewCard>
        </SubSection>

        <SubSection title="Posiciones">
          <NewCard>
            <div className="flex flex-wrap gap-3">
              {(['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'] as const).map((pos) => (
                <button
                  key={pos}
                  onClick={() => sileo.info({ title: pos, description: `Toast en posición ${pos}`, position: pos })}
                  className="h-[var(--btn-h)] px-3 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[length:var(--text-caption)] font-medium shadow-sm transition-colors"
                >
                  {pos}
                </button>
              ))}
            </div>
          </NewCard>
        </SubSection>

        {/* ===== 15. SPACING ===== */}
        <SectionTitle>15. Spacing Estándar</SectionTitle>

        <SubSection title="Paddings de página unificados">
          <NewCard label="Todos los valores vienen de CSS variables">
            <div className="overflow-x-auto">
              <table className="w-full text-[length:var(--text-body)]">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 pr-4 font-semibold text-neutral-900">Contexto</th>
                    <th className="text-left py-2 pr-4 font-semibold text-neutral-900">Mobile</th>
                    <th className="text-left py-2 font-semibold text-neutral-900">Desktop</th>
                  </tr>
                </thead>
                <tbody className="text-neutral-600">
                  <tr className="border-b border-neutral-100"><td className="py-2 pr-4 font-medium text-neutral-900">Page padding X</td><td className="py-2 pr-4">16px (px-4)</td><td className="py-2">32px (px-8)</td></tr>
                  <tr className="border-b border-neutral-100"><td className="py-2 pr-4 font-medium text-neutral-900">Page padding Y</td><td className="py-2 pr-4">16px (py-4)</td><td className="py-2">24px (py-6)</td></tr>
                  <tr className="border-b border-neutral-100"><td className="py-2 pr-4 font-medium text-neutral-900">Card padding</td><td className="py-2 pr-4">16px (p-4)</td><td className="py-2">24px (p-6)</td></tr>
                  <tr className="border-b border-neutral-100"><td className="py-2 pr-4 font-medium text-neutral-900">Section gap</td><td className="py-2 pr-4">16px (gap-4)</td><td className="py-2">24px (gap-6)</td></tr>
                  <tr><td className="py-2 pr-4 font-medium text-neutral-900">Element gap</td><td className="py-2 pr-4">12px (gap-3)</td><td className="py-2">12px (gap-3)</td></tr>
                </tbody>
              </table>
            </div>
          </NewCard>
        </SubSection>

        {/* Footer */}
        <div className="mt-16 mb-8 text-center">
          <p className="text-[length:var(--text-body)] text-neutral-400">
            Atlas Design System · Plus Jakarta Sans · CSS Custom Properties
          </p>
          <p className="text-[length:var(--text-caption)] text-neutral-400 mt-1">
            Todos los valores son editables cambiando las variables en <code className="bg-neutral-100 px-1.5 py-0.5 rounded-sm font-mono">index.css :root</code>
          </p>
        </div>
      </div>
    </div>
  );
}
